import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Upload, Sparkle, File, X, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CONTRACT_TYPES = ["Service Agreement","Lease Agreement","Maintenance Contract","Supply Agreement","Consulting Agreement","License Agreement","Other"];

export default function ContractForm() {
  const navigate = useNavigate();
  const ocrFileRef = useRef(null);
  const docFileRef = useRef(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrApplied, setOcrApplied] = useState(false);

  // Track the document file: either from OCR upload or manual attach
  const [documentFile, setDocumentFile] = useState(null);       // File object for upload
  const [documentStoredName, setDocumentStoredName] = useState(null); // already saved by OCR endpoint
  const [documentFileName, setDocumentFileName] = useState('');

  const [form, setForm] = useState({
    vendor_id: '', title: '', contract_type: '', start_date: '', end_date: '',
    contract_amount: '', renewal_terms: '', notes: '',
    is_recurring: false, recurring_amount: '', recurring_frequency: '',
    next_payment_date: '', recurring_end_date: ''
  });

  useEffect(() => {
    api.get('/vendors', { params: { status: 'active', limit: 500 } }).then(r => setVendors(r.data.items));
  }, []);

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // OCR: upload file → backend extracts text & stores file → returns extracted fields + stored filename
  const handleOCRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    setOcrApplied(false);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/contracts/ocr-extract', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setOcrResult(data);
      // The OCR endpoint already saved the file — track the stored name
      if (data.uploaded_file) {
        setDocumentStoredName(data.uploaded_file);
        setDocumentFileName(file.name);
        setDocumentFile(null); // no need to re-upload
      }
      toast.success('Document uploaded and analyzed. Review the extracted values below.');
    } catch (err) {
      // OCR failed but we still want to keep the file
      setDocumentFile(file);
      setDocumentFileName(file.name);
      setDocumentStoredName(null);
      toast.error('Text extraction failed, but the document is attached. Enter details manually.');
    } finally {
      setOcrLoading(false);
      if (ocrFileRef.current) ocrFileRef.current.value = '';
    }
  };

  // Manual file attach (no OCR)
  const handleManualAttach = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocumentFile(file);
    setDocumentFileName(file.name);
    setDocumentStoredName(null); // will be uploaded on save
    toast.success(`${file.name} attached. It will be saved with the contract.`);
    if (docFileRef.current) docFileRef.current.value = '';
  };

  const removeDocument = () => {
    setDocumentFile(null);
    setDocumentStoredName(null);
    setDocumentFileName('');
    setOcrResult(null);
    setOcrApplied(false);
  };

  const applyOCRValues = () => {
    if (!ocrResult) return;
    setForm(f => ({
      ...f,
      title: ocrResult.title || f.title,
      start_date: ocrResult.start_date || f.start_date,
      end_date: ocrResult.end_date || f.end_date,
      contract_amount: ocrResult.contract_amount ? String(ocrResult.contract_amount) : f.contract_amount,
      renewal_terms: ocrResult.renewal_terms || f.renewal_terms,
      recurring_frequency: ocrResult.recurring_frequency || f.recurring_frequency,
      recurring_amount: ocrResult.recurring_amount ? String(ocrResult.recurring_amount) : f.recurring_amount,
    }));
    setOcrApplied(true);
    toast.success('Extracted values applied to the form. Please review and confirm before saving.');
  };

  const handleSubmit = async () => {
    if (!form.vendor_id || !form.title) { toast.error('Vendor and title are required'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        contract_amount: form.contract_amount ? parseFloat(form.contract_amount) : null,
        recurring_amount: form.recurring_amount ? parseFloat(form.recurring_amount) : null,
      };
      const { data } = await api.post('/contracts', payload);
      const contractId = data.id;

      // Attach document to contract record
      if (documentStoredName && contractId) {
        // File already saved by OCR endpoint — just link it to the contract
        await api.put(`/contracts/${contractId}`, {
          file_path: documentStoredName,
          file_name: documentFileName
        });
      } else if (documentFile && contractId) {
        // File not yet saved — upload it now
        const fd = new FormData();
        fd.append('file', documentFile);
        await api.post(`/contracts/${contractId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      toast.success('Contract created successfully');
      navigate(`/contracts/${contractId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create contract');
    } finally { setLoading(false); }
  };

  const hasDocument = !!(documentFile || documentStoredName);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="contract-form-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">New Contract</h1>

      {/* Document Upload & OCR Section */}
      <Card className="rounded-sm border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Contract Document</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasDocument ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* OCR Upload */}
              <div
                className="border-2 border-dashed border-[#0055FF]/30 rounded-sm p-6 text-center cursor-pointer hover:border-[#0055FF]/60 hover:bg-[#0055FF]/5 transition-colors"
                onClick={() => ocrFileRef.current?.click()}
                data-testid="ocr-upload-zone"
              >
                <Sparkle size={28} className="mx-auto text-[#0055FF] mb-2" />
                <p className="text-sm font-medium text-[#0F172A]">Upload & Extract</p>
                <p className="text-xs text-[#475569] mt-1">Upload PDF to auto-extract contract fields</p>
                <input ref={ocrFileRef} type="file" className="hidden" accept=".pdf" onChange={handleOCRUpload} />
              </div>
              {/* Manual attach */}
              <div
                className="border-2 border-dashed border-[#E2E8F0] rounded-sm p-6 text-center cursor-pointer hover:border-[#475569]/40 hover:bg-[#F1F5F9] transition-colors"
                onClick={() => docFileRef.current?.click()}
                data-testid="manual-upload-zone"
              >
                <Upload size={28} className="mx-auto text-[#475569] mb-2" />
                <p className="text-sm font-medium text-[#0F172A]">Attach Document Only</p>
                <p className="text-xs text-[#475569] mt-1">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG</p>
                <input ref={docFileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleManualAttach} />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Attached document indicator */}
              <div className="flex items-center justify-between py-3 px-4 bg-[#DCFCE7] rounded-sm border border-[#22C55E]/30" data-testid="attached-document">
                <div className="flex items-center gap-3">
                  <File size={20} className="text-[#166534]" />
                  <div>
                    <p className="text-sm font-medium text-[#166534]">{documentFileName}</p>
                    <p className="text-xs text-[#166534]/70">
                      {documentStoredName ? 'Saved — will be linked to contract on save' : 'Will be uploaded on save'}
                    </p>
                  </div>
                </div>
                <button onClick={removeDocument} className="text-[#166534]/60 hover:text-[#991B1B] transition-colors p-1" data-testid="remove-document-btn">
                  <X size={16} />
                </button>
              </div>

              {/* OCR Results */}
              {ocrResult && (
                <div className="p-4 bg-[#F1F5F9] rounded-sm border" data-testid="ocr-results">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Sparkle size={16} className="text-[#0055FF]" />
                      <span className="text-sm font-semibold text-[#0F172A]">Extracted Fields</span>
                      <span className={`text-[10px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 rounded-sm ${
                        ocrResult.confidence === 'medium' ? 'bg-[#FEF9C3] text-[#854D0E]' : 'bg-[#FEE2E2] text-[#991B1B]'
                      }`}>
                        {ocrResult.confidence} confidence
                      </span>
                    </div>
                    {!ocrApplied ? (
                      <Button size="sm" className="rounded-sm bg-[#0055FF] hover:bg-[#0043CC] gap-1 h-8" onClick={applyOCRValues} data-testid="apply-ocr-btn">
                        <CheckCircle size={14} /> Apply to Form
                      </Button>
                    ) : (
                      <span className="text-xs text-[#166534] flex items-center gap-1"><CheckCircle size={14} /> Applied</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-[#475569]">
                    {ocrResult.title && <div><span className="text-[#94A3B8]">Title:</span> {ocrResult.title.slice(0, 80)}</div>}
                    {ocrResult.start_date && <div><span className="text-[#94A3B8]">Start:</span> {ocrResult.start_date}</div>}
                    {ocrResult.end_date && <div><span className="text-[#94A3B8]">End:</span> {ocrResult.end_date}</div>}
                    {ocrResult.contract_amount && <div><span className="text-[#94A3B8]">Amount:</span> {ocrResult.contract_amount}</div>}
                    {ocrResult.recurring_frequency && <div><span className="text-[#94A3B8]">Frequency:</span> {ocrResult.recurring_frequency}</div>}
                    {ocrResult.renewal_terms && <div><span className="text-[#94A3B8]">Renewal:</span> {ocrResult.renewal_terms}</div>}
                  </div>
                  <p className="text-[10px] text-[#94A3B8] mt-2 flex items-center gap-1">
                    <Warning size={12} /> Extracted values are suggestions. Review and edit fields before saving.
                  </p>
                </div>
              )}
            </div>
          )}

          {ocrLoading && (
            <div className="flex items-center gap-2 text-sm text-[#0055FF]">
              <div className="w-4 h-4 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" />
              Analyzing document...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contract Details Form */}
      <Card className="rounded-sm border">
        <CardHeader><CardTitle className="text-base font-semibold">Contract Details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Vendor</Label>
              <Select value={form.vendor_id} onValueChange={v => handleChange('vendor_id', v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="contract-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent className="rounded-sm max-h-60">{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Contract Type</Label>
              <Select value={form.contract_type} onValueChange={v => handleChange('contract_type', v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="contract-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="rounded-sm">{CONTRACT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Contract Title</Label>
            <Input value={form.title} onChange={e => handleChange('title', e.target.value)} className="rounded-sm mt-1" data-testid="contract-title-input" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Start Date</Label><Input type="date" value={form.start_date} onChange={e => handleChange('start_date', e.target.value)} className="rounded-sm mt-1" data-testid="contract-start-date" /></div>
            <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">End Date</Label><Input type="date" value={form.end_date} onChange={e => handleChange('end_date', e.target.value)} className="rounded-sm mt-1" data-testid="contract-end-date" /></div>
            <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Contract Amount (PHP)</Label><Input type="number" step="0.01" value={form.contract_amount} onChange={e => handleChange('contract_amount', e.target.value)} className="rounded-sm mt-1" data-testid="contract-amount-input" /></div>
          </div>

          <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Renewal Terms</Label><Input value={form.renewal_terms} onChange={e => handleChange('renewal_terms', e.target.value)} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Notes</Label><Textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} className="rounded-sm mt-1" rows={2} /></div>
        </CardContent>
      </Card>

      {/* Recurring Section */}
      <Card className="rounded-sm border">
        <CardHeader className="pb-2"><CardTitle className="text-base font-semibold flex items-center gap-3">Recurring Obligations<Switch checked={form.is_recurring} onCheckedChange={v => handleChange('is_recurring', v)} data-testid="recurring-toggle" /></CardTitle></CardHeader>
        {form.is_recurring && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Recurring Amount (PHP)</Label><Input type="number" step="0.01" value={form.recurring_amount} onChange={e => handleChange('recurring_amount', e.target.value)} className="rounded-sm mt-1" data-testid="recurring-amount-input" /></div>
              <div>
                <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Frequency</Label>
                <Select value={form.recurring_frequency} onValueChange={v => handleChange('recurring_frequency', v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="recurring-frequency-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="rounded-sm"><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Next Payment Date</Label><Input type="date" value={form.next_payment_date} onChange={e => handleChange('next_payment_date', e.target.value)} className="rounded-sm mt-1" data-testid="next-payment-date-input" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Recurring End Date</Label><Input type="date" value={form.recurring_end_date} onChange={e => handleChange('recurring_end_date', e.target.value)} className="rounded-sm mt-1" /></div>
          </CardContent>
        )}
      </Card>

      <div className="flex gap-3 justify-end">
        <Button variant="outline" className="rounded-sm" onClick={() => navigate('/contracts')} data-testid="cancel-btn">Cancel</Button>
        <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0043CC]" onClick={handleSubmit} disabled={loading} data-testid="save-contract-btn">{loading ? 'Saving...' : 'Save Contract'}</Button>
      </div>
    </div>
  );
}
